<template>
  <validation-observer v-if="editedRole" ref="observer" v-slot="{ changed, passes }">
    <b-modal
      id="editing-role-modal"
      ref="modal"
      :title="editedRole.id ? $t('user_roles.actions.edit') : $t('user_roles.actions.add')"
    >
      <b-form>
        <forms-validated-input
          type="relation"
          :label="$t('user_roles.fields.user')"
          :name="'user_id_' + (role ? role.id : 0)"
          :disabled="!canEditUser || disabled || loading"
          :rules="{
            required: true,
          }"
          :query="{
            slug: 'users',
            value: 'id',
            text: 'full_name',
            details: 'email',
          }"
          :min-search-length="0"
          :value="editedRole.user ? editedRole.user.id : null"
          :object-value="editedRole.user"
          mode="eager"
          @relation="setUser"
        />

        <forms-validated-input
          type="select"
          :value="editedRole.role"
          disabled
          :options="[
            { value: editedRole.role, text: capitalize($t(`loanables.roles.${editedRole.role}`)) },
          ]"
          name="role"
          :label="$t('user_roles.fields.role.title')"
        />

        <forms-validated-input
          v-if="!editedRole.id && editedRole.role === 'manager'"
          v-model="editedRole.remove_existing_coowner_roles"
          :label="$t('components.loanable.user_role_form.replace_roles')"
          name="remove_existing_coowner_roles"
          :description="$t('components.loanable.user_role_form.replace_roles_description')"
          type="checkbox"
          :disabled="disabled || loading"
        ></forms-validated-input>

        <forms-validated-input
          v-model="editedRole.title"
          :label="$t('components.loanable.user_role_form.title')"
          :name="'title_' + (role ? role.id : 0)"
          :description="$t('components.loanable.user_role_form.title_description')"
          type="text"
          :disabled="disabled || loading"
        />

        <forms-validated-input
          v-model="editedRole.show_as_contact"
          :label="$t('components.loanable.user_role_form.show_as_contact')"
          :name="'show_as_contact_' + (role ? role.id : 0)"
          type="checkbox"
          :disabled="disabled || loading"
          :description="$t('components.loanable.user_role_form.show_as_contact_description')"
        />
        <template v-if="canEditPaidAmounts">
          <forms-validated-input
            v-model="editedRole.pays_loan_price"
            :label="$t('components.loanable.user_role_form.pays_loan_price')"
            :name="'pays_loan_price_' + (role ? role.id : 0)"
            :description="$t('components.loanable.user_role_form.pays_loan_price_description')"
            type="checkbox"
            :disabled="disabled || loading"
          ></forms-validated-input>

          <template v-if="loanableHasPaidInsurance">
            <forms-validated-input
              v-model="editedRole.pays_loan_insurance"
              :label="$t('components.loanable.user_role_form.pays_loan_insurance')"
              :name="'pays_loan_insurance_' + (role ? role.id : 0)"
              :description="
                $t('components.loanable.user_role_form.pays_loan_insurance_description')
              "
              type="checkbox"
              :disabled="disabled || loading"
            ></forms-validated-input>

            <b-alert
              v-if="editedRole.role !== `owner` && !editedRole.pays_loan_insurance"
              variant="warning"
              show
            >
              <strong>{{ $t("components.loanable.user_role_form.insurance_warning") }}</strong>
            </b-alert>
          </template>
        </template>
      </b-form>
      <template #modal-footer>
        <div class="action-buttons">
          <icon-button
            role="save"
            :loading="loading"
            :disabled="disabled || loading || !changed"
            :onclick="async () => passes(save)"
          >
            {{ $t("forms.save") | capitalize }}
          </icon-button>
          <icon-button role="cancel" :disabled="disabled || loading" @click.prevent="cancel">
            {{ $t("forms.cancel") | capitalize }}
          </icon-button>
          <layout-loading v-if="loading" with-button />
        </div>
      </template>
    </b-modal>
  </validation-observer>
</template>

<script>
import FormsValidatedInput from "@/components/Forms/ValidatedInput.vue";
import IconButton from "@/components/shared/IconButton.vue";
import { capitalize } from "@/helpers/filters";
import { canEditRolePaidAmounts, canEditRoleUser } from "@/helpers/permissions/loanables";
import { post, put } from "@/requests/server";

export default {
  name: "LoanableUserRoleForm",
  components: { IconButton, FormsValidatedInput },
  props: {
    role: { type: Object, default: null },
    loanable: { type: Object, default: null },
    disabled: { type: Boolean, default: false },
    library: { type: Object, default: null },
  },
  data() {
    return {
      editedRole: {
        ...this.role,
      },
      loading: false,
    };
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    loanableHasPaidInsurance() {
      return this.library || this.loanable.type === "car";
    },
    canEditUser() {
      return this.role && canEditRoleUser(this.user, this.role);
    },
    canEditPaidAmounts() {
      return (
        this.role && canEditRolePaidAmounts(this.user, this.loanable ?? this.library, this.role)
      );
    },
  },
  methods: {
    capitalize,
    show() {
      this.$nextTick(() => {
        this.editedRole = {
          ...this.role,
        };
        this.$bvModal.show("editing-role-modal");
      });
    },
    async save() {
      if (!this.editedRole.user) {
        return;
      }
      if (!this.loanableHasPaidInsurance) {
        this.role.pays_loan_insurance = false;
      }

      let updatedRole = null;
      if (this.editedRole.id) {
        const { data } = await put(
          `/loanables/roles/${this.editedRole.id}`,
          {
            ...this.editedRole,
            user_id: this.editedRole.user.id,
          },
          {
            notifications: {
              action: this.$t("user_roles.notifications.update_role.action"),
              onSuccess: this.$t("user_roles.notifications.update_role.success"),
            },
          }
        );
        updatedRole = data;
      } else {
        let endpoint = "";
        if (this.library) {
          endpoint = "/libraries/" + this.library.id;
        } else {
          endpoint = "/loanables/" + this.loanable.id;
        }

        const { data } = await post(
          `${endpoint}/roles`,
          {
            ...this.editedRole,
            user_id: this.editedRole.user.id,
          },
          {
            notifications: {
              action: this.$t("user_roles.notifications.add_role.action"),
              onSuccess: this.$t("user_roles.notifications.add_role.success"),
            },
          }
        );
        updatedRole = data;
      }
      this.editedRole = {
        id: null,
        user: null,
        role: null,
      };
      this.$bvModal.hide("editing-role-modal");
      this.$emit("saved", updatedRole);
    },
    cancel() {
      this.$bvModal.hide("editing-role-modal");
    },
    setUser(user) {
      this.editedRole.user_id = user?.id;
      this.editedRole.user = user;
    },
  },
};
</script>

<style scoped>
.action-buttons {
  display: flex;
  gap: 0.5rem;
}
</style>
